/**
 * Safe navigation utilities to avoid popup blocker issues.
 * 
 * Browsers block window.open() calls that are not triggered by direct user interaction.
 * This utility uses dynamically created anchor elements which are more reliable.
 */

/**
 * Open a URL in a new tab without being blocked by popup blockers.
 * Creates a temporary anchor element and programmatically clicks it.
 * 
 * @param {string} url - The URL to open
 * @param {boolean} newTab - Whether to open in a new tab (default: true)
 */
export const safeOpenUrl = (url, newTab = true) => {
  if (!url) return;
  
  const link = document.createElement('a');
  link.href = url;
  if (newTab) {
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Open Google Maps directions to a location.
 * 
 * @param {number|null} latitude - The latitude coordinate
 * @param {number|null} longitude - The longitude coordinate
 * @param {string|null} address - Fallback address if coordinates not available
 */
export const openGoogleMapsNavigation = (latitude, longitude, address) => {
  let url = '';
  
  if (latitude && longitude) {
    url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
  } else if (address) {
    url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  }
  
  if (url) {
    safeOpenUrl(url);
  }
};

/**
 * Open Google Maps search for an address.
 * 
 * @param {string} address - The address to search for
 */
export const openGoogleMapsSearch = (address) => {
  if (!address) return;
  const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  safeOpenUrl(url);
};

/**
 * Open Waze navigation to a location.
 * 
 * @param {number|null} latitude - The latitude coordinate
 * @param {number|null} longitude - The longitude coordinate
 * @param {string|null} address - Fallback address if coordinates not available
 */
export const openWazeNavigation = (latitude, longitude, address) => {
  let url = '';
  
  if (latitude && longitude) {
    url = `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`;
  } else if (address) {
    url = `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;
  }
  
  if (url) {
    safeOpenUrl(url);
  }
};

export default {
  safeOpenUrl,
  openGoogleMapsNavigation,
  openGoogleMapsSearch,
  openWazeNavigation
};
